import type { PropsWithChildren } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useLogin } from 'contexts';
import type { CreateOnlyOfficeSessionArgs, DocumentLaunch } from 'types/documentEditing';
import { TAG_TYPES } from 'types/services';
import { documentStorageApi } from 'store/services/documentStorage';
import { getDocumentEditingOrigin } from 'utils/documentEditing/documentEditing';
import { isMobileApp } from 'utils/common';
import { DocumentEditor } from 'components/user/DocumentEditor/DocumentEditor';
import { DocumentEditingContext } from './DocumentEditingContext';

export const DocumentEditingProvider = ({ children }: PropsWithChildren) => {
  const { user } = useLogin();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();
  const [launch, setLaunch] = useState<DocumentLaunch | null>(null);
  const launchRef = useRef(launch);
  launchRef.current = launch;
  const closeRequestRef = useRef<(() => void) | null>(null);
  const previousOpen = useRef<DocumentLaunch | null>(null);
  const actor = user ? `${user.user_id}:${user.tenant_id}` : '';
  const origin = getDocumentEditingOrigin();
  const path = `${location.pathname}${location.search}${location.hash}`;
  const isOpen = Boolean(origin && actor && launch?.actor === actor && launch.editorKey === location.key);

  const refreshFiles = useCallback(() => dispatch(documentStorageApi.util.invalidateTags([TAG_TYPES.FILES])), [dispatch]);

  useLayoutEffect(() => {
    if (!launch || launch.editorKey || launch.actor !== actor) return;
    if (navigationType === 'PUSH' && location.key !== launch.sourceKey && path === launch.sourcePath) {
      const opened = { ...launch, editorKey: location.key };
      launchRef.current = opened;
      setLaunch(opened);
    }
  }, [launch, actor, navigationType, location.key, path]);

  useEffect(() => {
    if (launch && launch.actor !== actor) {
      launchRef.current = null;
      setLaunch(null);
    }
  }, [actor, launch]);

  useEffect(() => {
    if (isOpen) {
      previousOpen.current = launch;
      return;
    }
    const closed = previousOpen.current;
    previousOpen.current = null;
    if (!closed) return;
    refreshFiles();
    if (closed.actor === actor && path === closed.sourcePath && closed.focusElement?.isConnected) closed.focusElement.focus();
  }, [isOpen, launch, actor, path, refreshFiles]);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (launchRef.current?.editorKey && launchRef.current.actor === actor) refreshFiles();
    };
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, [actor, refreshFiles]);

  const openDocument = useCallback(
    (args: CreateOnlyOfficeSessionArgs, name: string): boolean => {
      const current = launchRef.current;
      if (!origin || !actor || isMobileApp() || (current && (!current.editorKey || current.editorKey === location.key))) return false;
      const next: DocumentLaunch = {
        args,
        name,
        sourceKey: location.key,
        sourcePath: path,
        editorKey: null,
        actor,
        focusElement: document.activeElement instanceof HTMLElement ? document.activeElement : null,
      };
      launchRef.current = next;
      setLaunch(next);
      try {
        navigate(path, { state: location.state, preventScrollReset: true });
        return true;
      } catch {
        launchRef.current = null;
        setLaunch(null);
        toast.error(t('user.documentEditor.unavailable'));
        return false;
      }
    },
    [actor, origin, location.key, location.state, path, navigate, t]
  );

  const close = useCallback(() => navigate(-1), [navigate]);
  const api = useMemo(() => ({ openDocument }), [openDocument]);

  return (
    <DocumentEditingContext.Provider value={api}>
      {children}
      {isOpen && launch && origin && (
        <Dialog
          open
          fullScreen
          aria-labelledby="document-editor-title"
          onKeyDown={(event) => {
            if (event.key === 'Escape') event.stopPropagation();
          }}
          onClose={(_event, reason) => {
            if (reason !== 'escapeKeyDown') return;
            if (closeRequestRef.current) closeRequestRef.current();
            else close();
          }}
          slotProps={{ paper: { sx: { overflow: 'hidden' } } }}>
          <DocumentEditor
            key={`${actor}:${launch.editorKey}`}
            args={launch.args}
            origin={origin}
            initialName={launch.name}
            onClose={close}
            closeNavigates
            closeRequestRef={closeRequestRef}
          />
        </Dialog>
      )}
    </DocumentEditingContext.Provider>
  );
};
